import { baseApi } from "./baseApi";

const LEAD_BASE_URL = "/vendor-leads";

export const vendorLeadApi = baseApi.injectEndpoints({
  tagTypes: ["Lead"],
  endpoints: (builder) => ({
    createLead: builder.mutation({
      query: (payload) => ({
        url: LEAD_BASE_URL,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Lead", id: "LIST" }],
    }),

    getLeadList: builder.query({
      query: (params) => ({
        url: LEAD_BASE_URL,
        method: "GET",
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({ type: "Lead", id: _id })),
              { type: "Lead", id: "LIST" },
            ]
          : [{ type: "Lead", id: "LIST" }],
    }),

    getVendorLeadQuotationPdfExcel: builder.query({
      query: ({ id, ...params }) => ({
        url: `${LEAD_BASE_URL}/vendor-quotation-pdf-excel/${id}`,
        method: "GET",
        params,
      }),
      providesTags: (result, error, { id }) => [
        { type: "Lead", id: `QUOTATION_PDF_EXCEL_${id}` },
      ],
    }),

    // For showing average won time in won tab in vendor lead detail screen
    getWonStats: builder.query({
      query: (params) => ({
        url: `${LEAD_BASE_URL}/won-stats`,
        method: "GET",
        params,
      }),
      providesTags: [{ type: "Lead", id: "WON_STATS" }],
    }),

    getLeadDetail: builder.query({
      query: (id) => `${LEAD_BASE_URL}/${id}`,
      providesTags: (result, error, id) => [
        { type: "Lead", id },
        { type: "Lead", id: "LIST" },
      ],
    }),

    updateLeadStatus: builder.mutation({
      query: ({ id, body }) => ({
        url: `${LEAD_BASE_URL}/${id}/status`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Lead", id },
        { type: "Lead", id: "LIST" },
        { type: "Note", id: "LIST" },
      ],
    }),

    updateLead: builder.mutation({
      query: (payload) => {
        const { _id, ...body } = payload;
        return {
          url: `${LEAD_BASE_URL}/${_id}`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: (result, error, { _id }) => [
        { type: "Lead", id: _id },
        { type: "Lead", id: "LIST" },
      ],
    }),

    updateLeadOnboarding: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `${LEAD_BASE_URL}/${id}/onboarding`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Lead", id },
        { type: "Lead", id: "LIST" },
      ],
    }),

    getLeadStatusLog: builder.query({
      query: ({ id, ...params }) => ({
        url: `${LEAD_BASE_URL}/${id}/status-log`,
        method: "GET",
        params,
      }),
      providesTags: (result, error, { id }) => [{ type: "Lead", id }],
    }),

    getStatusLogReport: builder.query({
      query: (params) => ({
        url: `${LEAD_BASE_URL}/reports/status-log`,
        method: "GET",
        params,
      }),
      providesTags: [{ type: "Lead", id: "STATUS_LOG_REPORT" }],
    }),

    bulkImportLeads: builder.mutation({
      query: (payload) => ({
        url: `${LEAD_BASE_URL}/bulk-import`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Lead", id: "LIST" }],
    }),

    searchLeads: builder.query({
      query: ({ q }) => ({
        url: `${LEAD_BASE_URL}/search`,
        params: { q },
      }),
    }),

    assignResponsible: builder.mutation({
      query: (payload) => ({
        url: `${LEAD_BASE_URL}/assign-responsible`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: [{ type: "Lead", id: "LIST" }],
    }),

    getMyFollowUps: builder.query({
      query: () => `${LEAD_BASE_URL}/my-follow-ups`,
      providesTags: [{ type: "Lead", id: "MY_FOLLOW_UPS" }],
      invalidatesTags: (result, error, { id }) => [
        { type: "Lead", id },
        { type: "Lead", id: "LIST" },
        { type: "Note", id: "LIST" },
        { type: "Lead", id: "MY_FOLLOW_UPS" },
      ],
    }),

    getLeadStepLog: builder.query({
      query: ({ id, ...params }) => ({
        url: `${LEAD_BASE_URL}/${id}/step-log`,
        method: "GET",
        params,
      }),
      providesTags: (result, error, { id }) => [{ type: "Lead", id }],
    }),

    getVendorLeadQuotationPdfExcelList: builder.query({
      query: (params) => ({
        url: `${LEAD_BASE_URL}/vendor-quotation-lead-list`,
        method: "GET",
        params,
      }),
      providesTags: [{ type: "Lead", id: "QUOTATION_PDF_EXCEL_LIST" }],
    }),
    listLeadVendorQuotation: builder.query({
      query: (params) => ({
        url: `${LEAD_BASE_URL}/list-lead-vendor-quotation`,
        method: "GET",
        params,
      }),
      providesTags: [{ type: "Lead", id: "QUOTATION_PDF_EXCEL_LIST" }],
    }),
    exportLeadVendorQuotation: builder.query({
      query: (params) => ({
        url: `${LEAD_BASE_URL}/list-lead-vendor-quotation-excel`,
        method: "GET",
        params,
      }),
      providesTags: [{ type: "Lead", id: "QUOTATION_PDF_EXCEL_LIST" }],
    }),
  }),
});

export const {
  useCreateLeadMutation,
  useLazyListLeadVendorQuotationQuery,
  useLazyExportLeadVendorQuotationQuery,
  useLazyGetLeadListQuery,
  useGetLeadDetailQuery,
  useUpdateLeadMutation,
  useUpdateLeadStatusMutation,
  useUpdateLeadOnboardingMutation,
  useLazyGetLeadStatusLogQuery,
  useBulkImportLeadsMutation,
  useLazyGetStatusLogReportQuery,
  useLazySearchLeadsQuery,
  useAssignResponsibleMutation,
  useLazyGetLeadStepLogQuery,
  useLazyGetMyFollowUpsQuery,
  useLazyGetWonStatsQuery,
  useLazyGetVendorLeadQuotationPdfExcelListQuery,
  useLazyGetVendorLeadQuotationPdfExcelQuery,
} = vendorLeadApi;
